import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Check, X } from 'lucide-react';
import { acceptDrafts, listDrafts, rejectDrafts, updateDraft, type TimeDraft } from '@mkcore/time/schema';
import { Badge, Button, EmptyState, ErrorState, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatDuration, formatTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { ProjectSelect, WorkTypeSelect } from '@/features/time/pickers';

const search = z.object({ from: z.string().optional(), to: z.string().optional() });
export const Route = createFileRoute('/_app/time/drafts')({ validateSearch: search, component: Drafts });

type Draft = z.output<typeof TimeDraft>;

/**
 * Concept hours (CO-0250): what agents propose from the calendar, meetings and other sources. Nothing counts until you
 * accept it; complete the project and work type first where the agent did not know them.
 */
function Drafts() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['time', 'draft', 'list', s.from, s.to], queryFn: () => call(listDrafts, { status: 'open', ...(s.from && { from: s.from }), ...(s.to && { to: s.to }) }) });
  const invalidate = [['time']];
  const accept = useActionMutation(acceptDrafts, {
    invalidate,
    onSuccess: (r) => {
      const ok = r.results.filter((x) => x.ok).length;
      const failed = r.results.filter((x) => !x.ok);
      if (ok) toast.success(t('time.drafts.accepted', { count: ok }));
      if (failed.length) toast.error(failed.map((f) => f.error).join('; '));
    },
  });
  const reject = useActionMutation(rejectDrafts, { invalidate, success: (r) => t('time.drafts.rejected', { count: r.rejected }) });
  const items = q.data?.items ?? [];
  const complete = items.filter((d) => d.projectId && d.workTypeId);
  return (
    <>
      <PageHeader
        title={t('time.drafts.title')}
        description={t('time.drafts.description')}
        actions={
          complete.length > 1 && (
            <Button loading={accept.isPending} onClick={() => accept.mutate({ input: { ids: complete.map((d) => d.id) } })}>
              <Check /> {t('time.drafts.acceptAll', { count: complete.length })}
            </Button>
          )
        }
      />
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-48" />
      ) : !items.length ? (
        <EmptyState title={t('time.drafts.none')} description={t('time.drafts.noneHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('time.drafts.when')}</Th>
              <Th>{t('time.drafts.what')}</Th>
              <Th className="min-w-56">{t('time.drafts.booking')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {items.map((d) => (
              <DraftRow key={d.id} draft={d} onAccept={() => accept.mutate({ input: { ids: [d.id] } })} onReject={() => reject.mutate({ input: { ids: [d.id] } })} busy={accept.isPending || reject.isPending} />
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}

function DraftRow({ draft: d, onAccept, onReject, busy }: { draft: Draft; onAccept: () => void; onReject: () => void; busy: boolean }) {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState(d.projectId ?? '');
  const [workTypeId, setWorkTypeId] = useState(d.workTypeId ?? '');
  const update = useActionMutation(updateDraft, { invalidate: [['time', 'draft']] });
  const save = (patch: { projectId?: string; workTypeId?: string }) =>
    update.mutate({ input: { id: d.id, expectedVersion: d.version, ...(patch.projectId !== undefined && { projectId: patch.projectId || null }), ...(patch.workTypeId !== undefined && { workTypeId: patch.workTypeId || null }) } });
  return (
    <Tr aria-label={d.description || d.sourceRef || d.id}>
      <Td className="whitespace-nowrap tabular">
        <Link to="/time/day/$date" params={{ date: d.date }} className="font-bold hover:underline" title={t('time.day.open')}>
          {formatDate(d.date)}
        </Link>
        <div className="text-xs text-fg-3">
          {d.timed ? `${formatTime(d.startedAt)}–${formatTime(d.endedAt)} · ` : ''}
          {formatDuration(d.durationSeconds)}
        </div>
      </Td>
      <Td>
        <div>{d.description || '—'}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-fg-3">
          <Badge tone="outline">{t(`time.drafts.source.${d.source}`)}</Badge>
          <span>{t('time.drafts.by', { name: d.proposedByName })}</span>
          {d.reason && <span className="italic">· {d.reason}</span>}
        </div>
      </Td>
      <Td>
        <div className="grid gap-1.5">
          <ProjectSelect
            value={projectId}
            aria-label={t('time.fields.project')}
            emptyLabel={t('time.drafts.chooseProject')}
            onChange={(v) => {
              setProjectId(v);
              save({ projectId: v });
            }}
          />
          <WorkTypeSelect
            value={workTypeId}
            aria-label={t('time.fields.workType')}
            onChange={(v) => {
              setWorkTypeId(v);
              save({ workTypeId: v });
            }}
          />
        </div>
      </Td>
      <Td className="text-right">
        <span className="inline-flex gap-1">
          <Button size="sm" disabled={!projectId || !workTypeId || update.isPending} loading={busy} onClick={onAccept}>
            <Check /> {t('time.drafts.accept')}
          </Button>
          <Button size="sm" variant="ghost" aria-label={t('time.drafts.reject')} disabled={busy} onClick={onReject}>
            <X />
          </Button>
        </span>
      </Td>
    </Tr>
  );
}
